import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  initializeAuth,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserPopupRedirectResolver,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import { getPerformance } from 'firebase/performance';
import { environment } from '@environments/environment';

import {
  FIREBASE_APP,
  FIREBASE_APP_CHECK,
  FIREBASE_AUTH,
  FIREBASE_FUNCTIONS,
  FIREBASE_PERFORMANCE,
  FIREBASE_STORAGE,
  FIRESTORE,
} from '@core/firebase/firebase.tokens';

const shouldUseEmulators =
  !environment.production && environment.useEmulators && environment.emulators;

export function provideFirebase(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: FIREBASE_APP,
      useFactory: (): FirebaseApp => initializeApp({ ...environment.firebase }),
    },
    {
      provide: FIREBASE_AUTH,
      useFactory: (app: FirebaseApp) => {
        // Match prior @angular/fire `provideAuth` setup from `main` (required
        // for reliable `signInWithPopup` with Microsoft in this app).
        const auth = initializeAuth(app, {
          persistence: [indexedDBLocalPersistence, browserLocalPersistence],
          popupRedirectResolver: browserPopupRedirectResolver,
        });
        if (shouldUseEmulators) {
          connectAuthEmulator(
            auth,
            `http://${environment.emulators.auth.host}:${environment.emulators.auth.port}`,
            { disableWarnings: true }
          );
        }
        return auth;
      },
      deps: [FIREBASE_APP],
    },
    {
      provide: FIRESTORE,
      useFactory: (app: FirebaseApp) => {
        const firestore = getFirestore(app);
        if (shouldUseEmulators) {
          connectFirestoreEmulator(
            firestore,
            environment.emulators.firestore.host || 'localhost',
            environment.emulators.firestore.port || 8080
          );
        }
        return firestore;
      },
      deps: [FIREBASE_APP],
    },
    {
      provide: FIREBASE_STORAGE,
      useFactory: (app: FirebaseApp) => {
        const storage = getStorage(app);
        if (shouldUseEmulators) {
          const storageEmulator = environment.emulators.storage;
          connectStorageEmulator(
            storage,
            storageEmulator?.host || 'localhost',
            storageEmulator?.port ?? 9199
          );
        }
        return storage;
      },
      deps: [FIREBASE_APP],
    },
    {
      provide: FIREBASE_FUNCTIONS,
      useFactory: (app: FirebaseApp) => {
        const functions = getFunctions(app);
        if (shouldUseEmulators) {
          connectFunctionsEmulator(
            functions,
            environment.emulators.functions.host || 'localhost',
            environment.emulators.functions.port || 5001
          );
        }
        return functions;
      },
      deps: [FIREBASE_APP],
    },
    {
      provide: FIREBASE_APP_CHECK,
      useFactory: (app: FirebaseApp) => {
        if (!environment.production && environment.appCheck?.debug?.token) {
          (
            self as unknown as {
              FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string;
            }
          ).FIREBASE_APPCHECK_DEBUG_TOKEN = environment.appCheck.debug.token;
        }
        const provider = new ReCaptchaEnterpriseProvider(
          environment.recaptcha.siteKey
        );
        return initializeAppCheck(app, {
          provider,
          isTokenAutoRefreshEnabled: true,
        });
      },
      deps: [FIREBASE_APP],
    },
    {
      provide: FIREBASE_PERFORMANCE,
      useFactory: (app: FirebaseApp) => getPerformance(app),
      deps: [FIREBASE_APP],
    },
  ]);
}
